import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useRoom } from "../hooks/useVideo";
import { AllowGuestsToggle } from "./AllowGuestsToggle";
import { AdmitPolicySelect } from "./AnyoneCanAdmitToggle";
import { WaitingRoomToggle } from "./GuestsJoinDirectlyToggle";

interface InCallInviteContentProps {
  roomId: string;
}

/**
 * Rendered inside MeetingControls' Room settings popover (owner-only).
 *
 * Layout matches the design's own Room settings popover exactly
 * (weco2/call.jsx): AllowGuestsToggle, then a single indented/bordered
 * column holding WaitingRoomToggle + AdmitPolicySelect together (one
 * indent level, not two). Both nested pieces always render for an owner —
 * they dim + become non-interactive (`opacity-40 pointer-events-none`)
 * when their parent toggle is off, rather than unmounting, so the popover
 * never jumps and always shows the full settings hierarchy.
 */
export function InCallInviteContent({ roomId }: InCallInviteContentProps) {
  const { data: room } = useRoom(roomId, { pollIntervalMs: false });
  const queryClient = useQueryClient();

  const isOwner = Boolean(room?.is_administrable);

  // All three states are tracked locally so conditional dimming reacts
  // immediately (optimistic), without waiting for a round-trip query
  // invalidation after each PATCH.
  const [allowGuests, setAllowGuests] = useState(() => Boolean(room?.allow_guests));
  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState(
    () => room?.waiting_room_enabled ?? false,
  );
  const [admitPolicy, setAdmitPolicy] = useState<"owners" | "admins" | "participants">(
    () => room?.admit_policy ?? "admins",
  );

  useEffect(() => {
    if (room?.allow_guests !== undefined) setAllowGuests(Boolean(room.allow_guests));
  }, [room?.allow_guests]);

  useEffect(() => {
    if (room?.waiting_room_enabled !== undefined) setWaitingRoomEnabled(room.waiting_room_enabled);
  }, [room?.waiting_room_enabled]);

  useEffect(() => {
    if (room?.admit_policy !== undefined) setAdmitPolicy(room.admit_policy);
  }, [room?.admit_policy]);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["video", "room", roomId] });

  if (!isOwner) return null;

  return (
    <>
      <AllowGuestsToggle
        roomId={roomId}
        initial={allowGuests}
        onChange={(next) => {
          setAllowGuests(next);
          invalidate();
        }}
      />
      <div
        className={`ps-4 ms-2 border-s border-border space-y-4 ${!allowGuests ? "opacity-40 pointer-events-none" : ""}`}
      >
        <WaitingRoomToggle
          roomId={roomId}
          initial={waitingRoomEnabled}
          onChange={(next) => {
            setWaitingRoomEnabled(next);
            invalidate();
          }}
        />
        <div className={!waitingRoomEnabled ? "opacity-40 pointer-events-none" : ""}>
          <AdmitPolicySelect
            roomId={roomId}
            initial={admitPolicy}
            onChange={(next) => {
              setAdmitPolicy(next);
              invalidate();
            }}
          />
        </div>
      </div>
    </>
  );
}
